import React, { useEffect, useState } from "react";
import {
  startStream,
  pauseStream,
  resumeStream,
  skipTrack,
  getStatus,
  getPlaybackMode,
  setPlaybackMode,
} from "../../services/adminStreamApi";

export default function AdminStreamControl() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playbackMode, setPlaybackModeState] = useState("LOCAL");

  const refreshStatus = async () => {
    const [statusRes, modeRes] = await Promise.all([
      getStatus(),
      getPlaybackMode(),
    ]);

    setStatus(statusRes.data);
    setPlaybackModeState(modeRes.data.mode);
  };

  useEffect(() => {
    refreshStatus();
    const i = setInterval(refreshStatus, 5000);
    return () => clearInterval(i);
  }, []);

  const action = async (fn) => {
    try {
      setLoading(true);
      await fn();
      await refreshStatus();
    } finally {
      setLoading(false);
    }
  };

  const isLive = status?.isPlaying;

  return (
    <div className="flex justify-center px-4">
      <div className="w-full max-w-xl bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800">
        {/* Header */}
        <h2 className="text-2xl font-bold text-white text-center mb-6">
          🎛️ Live Stream Control
        </h2>
        {/* Playback Mode Switch */}
        <div className="bg-slate-950 rounded-xl p-4 mb-6 border border-slate-800">
          <p className="text-slate-400 text-sm mb-3">Playback Mode</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              disabled={loading || playbackMode === "STREAM"}
              onClick={() => action(() => setPlaybackMode("STREAM"))}
              className={`rounded-xl py-3 font-semibold transition ${
                playbackMode === "STREAM"
                  ? "bg-emerald-500 text-black"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              Live Stream
            </button>

            <button
              disabled={loading || playbackMode === "LOCAL"}
              onClick={() => action(() => setPlaybackMode("LOCAL"))}
              className={`rounded-xl py-3 font-semibold transition ${
                playbackMode === "LOCAL"
                  ? "bg-sky-500 text-black"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              Playlist
            </button>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-slate-950 rounded-xl p-4 mb-6 space-y-2 border border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Status</span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isLive
                  ? "bg-green-500/20 text-green-400"
                  : "bg-yellow-500/20 text-yellow-400"
              }`}
            >
              {isLive ? "LIVE" : "PAUSED"}
            </span>
          </div>

          <div>
            <p className="text-slate-400 text-sm">Track</p>
            <p className="text-white font-medium">
              {status?.currentTrack?.title || "—"}
            </p>
          </div>

          <div>
            <p className="text-slate-400 text-sm">Artist</p>
            <p className="text-white font-medium">
              {status?.currentTrack?.artist || "—"}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => action(startStream)}
            disabled={loading}
            className="rounded-xl py-3 font-semibold bg-emerald-500 text-black hover:bg-emerald-400 transition disabled:opacity-50"
          >
            ▶ Start
          </button>

          <button
            onClick={() => action(pauseStream)}
            disabled={loading}
            className="rounded-xl py-3 font-semibold bg-amber-500 text-black hover:bg-amber-400 transition disabled:opacity-50"
          >
            ⏸ Pause
          </button>

          <button
            onClick={() => action(resumeStream)}
            disabled={loading}
            className="rounded-xl py-3 font-semibold bg-sky-500 text-black hover:bg-sky-400 transition disabled:opacity-50"
          >
            ▶ Resume
          </button>

          <button
            onClick={() => action(skipTrack)}
            disabled={loading}
            className="rounded-xl py-3 font-semibold bg-rose-500 text-white hover:bg-rose-400 transition disabled:opacity-50"
          >
            ⏭ Skip
          </button>
        </div>
      </div>
    </div>
  );
}
